import React from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useCreateDashboard } from "api/api-hooks/dashboards-hooks";
import { CreateDashboardRequest, IMPLEMENTATION } from "api/model/dashboard/Dashboard";
import {
  ControllerDropdownInput,
  ControllerTextInput,
} from "components/basic/controller-inputs/ControllerInputs";
import { Button } from "primereact/button";
import { SelectItem } from "primereact/selectitem";
import { Controller, useForm } from "react-hook-form";
import { T } from "services/lang/LanguageService";
import { z } from "zod";

const schema = z.object({
  title: z
    .string({ message: T("word.required-fields") })
    .trim()
    .nonempty(),
  implementation: z.enum(Object.keys(IMPLEMENTATION) as [string, ...string[]]),
});

type CreateDashboardFormProps = {
  onClose: () => void;
};

const CreateDashboardForm: React.FC<CreateDashboardFormProps> = ({ onClose }) => {
  const { mutate: createDashboardMutation, isPending } = useCreateDashboard({
    onSuccess: onClose,
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<CreateDashboardRequest>({
    resolver: zodResolver(schema),
  });

  const onSubmit = (formData: CreateDashboardRequest) => {
    createDashboardMutation(formData);
  };

  const implementationOptions = Object.entries(IMPLEMENTATION).reduce((items, [key, value]) => {
    if (key === "STANDARD") return items;
    items.push({
      label: value,
      value: key,
    });
    return items;
  }, [] as SelectItem[]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="flex flex-wrap">
        <Controller
          name="title"
          control={control}
          render={({ field }) => (
            <ControllerTextInput field={field} errors={errors} label={T("word.title")} col={12} />
          )}
        />
      </div>
      <div className="flex flex-wrap">
        <Controller
          name="implementation"
          defaultValue="JS"
          control={control}
          render={({ field }) => (
            <ControllerDropdownInput
              field={field}
              errors={errors}
              label={T("word.type")}
              col={12}
              options={implementationOptions}
            />
          )}
        />
      </div>
      <div className="mt- 2 flex flex-wrap gap-2 justify-content-end">
        <Button
          type="submit"
          label={T("action.create")}
          loading={isPending}
          className="p-button-rounded p-button-success w-10rem"
        />
        <Button
          type="button"
          onClick={onClose}
          label={T("word.cancel")}
          rounded
          severity="secondary"
          outlined
          className="w-10rem"
        />
      </div>
    </form>
  );
};

export default CreateDashboardForm;
